// The « aide-nous » prompt (_review_popup.erb): what the visitor came for, what held them back or what
// they missed, kept as a Feedback with the MOMENT it was asked at.
// - The discreet pill shows once the visitor scrolled past the map (moment `pill`).
// - In the treatment of the A/B surface feedback_prompt, the question is also asked at the moments:
//   2 s after a save (`after_save`, on the ride page the save leads to), at the 2nd paywall refusal of the visit (`paywall_refusal`), when
//   leaving with an unsaved route on desktop (`exit`). The control is only exposed, the interventions'
//   holdout neither. Once per visit, never over another prompt or while drawing (PromptBudget).
import I18n from '@js/modules/translations';
import PromptBudget from '@js/modules/promptBudget';
import Orchestrator from '@js/modules/orchestrator';
import AnalyticsService from '@js/modules/analyticsService';

import Notification from '@js/components/layouts/notification';

import {sendReview} from '@js/actions/reviewActions';
import {trackExperimentExposure} from '@js/actions/trackingActions';
import {isFailure, needsErrorToast} from '@js/modules/apiFailure';

// The question asked at each moment (Feedback::KIND_BY_MOMENT).
const KIND_BY_MOMENT = {pill: 'intent', after_save: 'intent', paywall_refusal: 'paywall', exit: 'missing'};
const DAY_MS = 24 * 60 * 60 * 1000;
const PAYWALL_REFUSALS_KEY = 'feedback-paywall-refusals';
// A save leaves for the ride page: the question waits there.
const AFTER_SAVE_KEY = 'feedback-after-save';

class ReviewManager {
    _reviewPopupElement = 'review-popup';
    _reviewModalElement = 'review-modal';

    _selectedRating = 0;
    _moment = 'pill';

    _delayBeforePopup = 5000;
    _daysSinceDismissed = 3;
    // Asked at a moment again only this long after an answer.
    _daysSinceAnswered = 30;
    _afterSaveDelay = 2000;
    _paywallRefusalsBeforeAsking = 2;

    _minScrollDistance = 500;
    _hasScrolledEnough = false;

    _homePlanningBlockSelector = '.main-section.home-map';

    // Listeners are bound once: rebinding them on each show sent several reviews per submit.
    _pillBound = false;
    _modalBound = false;
    _momentListeners = [];
    _timers = [];
    _pageItems = {};

    /* Public methods
    ******************** */
    initialize = () => {
        this._listenToMoments();

        if (this._hasUserReviewed()) {
            return;
        }

        this._timers.push(setTimeout(() => {
            this._initScrollListener();
        }, this._delayBeforePopup));
    };

    /* Private methods
    ******************** */
    // Only for a visitor drawn in a running feedback_prompt experiment, outside the interventions' holdout.
    _listenToMoments = () => {
        if (!window.experiments?.feedback_prompt || !Orchestrator.active()) {
            return;
        }

        if (this._safeSession(AFTER_SAVE_KEY)) {
            this._safeSession(AFTER_SAVE_KEY, '');
            this._askLater('after_save', this._afterSaveDelay);
        }
        this._on('mapplanner:ride-saved', () => {
            this._safeSession(AFTER_SAVE_KEY, '1');
            this._askLater('after_save', this._afterSaveDelay);
        });
        this._on('mapplanner:paywall-dismissed', () => {
            const refusals = Number(this._safeSession(PAYWALL_REFUSALS_KEY) || 0) + 1;
            this._safeSession(PAYWALL_REFUSALS_KEY, String(refusals));
            if (refusals === this._paywallRefusalsBeforeAsking) {
                this._askLater('paywall_refusal', 0);
            }
        });
        this._on('mapplanner:exit-intent', (event) => {
            if (event.detail?.desktop) {
                this._ask('exit');
            }
        });
    };

    _on = (type, listener) => {
        window.addEventListener(type, listener);
        this._momentListeners.push([type, listener]);
    };

    // After +delay+, or once another prompt's spacing has passed.
    _askLater = (moment, delay) => {
        const wait = PromptBudget.delayBeforeAsk();
        if (wait === null) {
            return;
        }

        this._timers.push(setTimeout(() => this._ask(moment), Math.max(delay, wait)));
    };

    // The exposure is where the variant decides: the control would have been asked here (the same claim, dry).
    _ask = (moment) => {
        const treatment = window.experimentParams?.feedback_prompt === true;
        if (this._isModalOpen() || this._answeredRecently()
            || !PromptBudget.claim('feedback', {dryRun: !treatment, onPreempt: this._closeModal, moment}).ok) {
            return;
        }

        trackExperimentExposure('feedback_prompt');
        if (!treatment) {
            return;
        }

        if (moment === 'after_save') {
            this._safeSession(AFTER_SAVE_KEY, '');
        }
        this._openModal(moment);
    };

    _initScrollListener = () => {
        this._handleScroll = () => {
            const scrolledEnough = this._hasScrolledPastForm();

            if (scrolledEnough && !this._hasScrolledEnough) {
                this._hasScrolledEnough = true;
                this._showReviewPopup();
            } else if (!scrolledEnough && this._hasScrolledEnough) {
                this._hasScrolledEnough = false;
                this._hidePopup();
            }
        };

        window.addEventListener('scroll', this._handleScroll, {passive: true});
    };

    _stopScrollListener = () => {
        if (this._handleScroll) {
            window.removeEventListener('scroll', this._handleScroll);
            this._handleScroll = null;
        }
    };

    _hasScrolledPastForm = () => {
        const block = document.querySelector(this._homePlanningBlockSelector);
        if (block) {
            return block.getBoundingClientRect().bottom <= 0;
        }

        return (window.scrollY || window.pageYOffset) >= this._minScrollDistance;
    };

    // localStorage can throw (SecurityError / NS_ERROR_FAILURE) in private mode, in-app browsers or
    // when storage is blocked — never let that bubble up and break the page.
    _safeGetItem = (key) => {
        if (key in this._pageItems) {
            return this._pageItems[key];
        }
        try {
            return window.localStorage.getItem(key);
        } catch {
            return null;
        }
    };

    // A write that fails is remembered for the page.
    _safeSetItem = (key, value) => {
        try {
            window.localStorage.setItem(key, value);
            delete this._pageItems[key];
        } catch {
            this._pageItems[key] = value;
        }
    };

    // No storage, no popup: an answer that can't be read would be asked again on every visit.
    _storageUnreadable = () => {
        try {
            window.localStorage.getItem('review_submitted');
            return false;
        } catch {
            return true;
        }
    };

    // Reads (one argument) or writes (two) the session: the paywall refusals of the visit.
    _safeSession = (key, value) => {
        try {
            if (value === undefined) {
                return window.sessionStorage.getItem(key);
            }
            window.sessionStorage.setItem(key, value);
        } catch {
            return null;
        }

        return value;
    };

    _hasUserReviewed = () => {
        const reviewed = this._safeGetItem('review_submitted');
        const dismissed = this._safeGetItem('review_dismissed');
        const dismissedTime = this._safeGetItem('review_dismissed_time');

        if (reviewed === 'true' || this._storageUnreadable()) {
            return true;
        }

        // Wait for 3 days before showing the popup again
        if (dismissed === 'true' && dismissedTime) {
            const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / DAY_MS;
            return daysSinceDismissed < this._daysSinceDismissed;
        }

        return false;
    };

    _answeredRecently = () => {
        const answeredAt = parseInt(this._safeGetItem('feedback_answered_at') || 0);

        return this._storageUnreadable() || (answeredAt > 0 && (Date.now() - answeredAt) / DAY_MS < this._daysSinceAnswered);
    };

    _isModalOpen = () => {
        const $modal = document.getElementById(this._reviewModalElement);

        return !!$modal && !$modal.classList.contains('hidden');
    };

    _showReviewPopup = () => {
        const $reviewPopup = document.getElementById(this._reviewPopupElement);
        if (!$reviewPopup) {
            return;
        }

        $reviewPopup.classList.remove('hidden');

        if (this._pillBound) {
            return;
        }
        this._pillBound = true;

        $reviewPopup.querySelector('.review-popup-mini')?.addEventListener('click', (event) => {
            if (!event.target.classList.contains('review-popup-close')) {
                PromptBudget.markAsked();
                this._openModal('pill');
            }
        });

        $reviewPopup.querySelector('.review-popup-close')?.addEventListener('click', (event) => {
            event.stopPropagation();
            this._dismissPopup();
        });
    };

    // Shows the question of the moment, the others stay hidden.
    _openModal = (moment = 'pill') => {
        const $modal = document.getElementById(this._reviewModalElement);
        if (!$modal) {
            return;
        }

        this._moment = moment;
        $modal.querySelectorAll('.review-intent').forEach(($fieldset) => {
            $fieldset.classList.toggle('hidden', $fieldset.dataset.kind !== KIND_BY_MOMENT[moment]);
        });

        $modal.classList.remove('hidden');
        this._initModalEvents();
        // It may open by itself (a moment): the keyboard goes into it, Escape closes it, and the focus
        // comes back where it was.
        this._focusBefore = document.activeElement;
        ($modal.querySelector('.review-intent:not(.hidden) input') || $modal.querySelector('.review-modal-close'))?.focus();
        document.addEventListener('keydown', this._onModalKeydown);
        AnalyticsService.trackFeedbackPromptShown(moment);
    };

    // A modal dialog: Escape closes it, Tab stays inside it (the hidden questions excepted).
    _onModalKeydown = (event) => {
        if (event.key === 'Escape') {
            this._dismissModal();

            return;
        }

        const $dialog = document.getElementById(this._reviewModalElement)?.querySelector('.review-modal-content');
        if (event.key !== 'Tab' || !$dialog) {
            return;
        }

        const focusables = [...$dialog.querySelectorAll('button:not([disabled]), a[href], input, textarea, [tabindex]:not([tabindex="-1"])')]
            .filter(($element) => !$element.closest('.hidden'));
        if (!focusables.length) {
            return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const inside = $dialog.contains(document.activeElement);

        if (event.shiftKey && (!inside || document.activeElement === first)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (!inside || document.activeElement === last)) {
            event.preventDefault();
            first.focus();
        }
    };

    _closeModal = () => {
        const $modal = document.getElementById(this._reviewModalElement);
        if ($modal) {
            $modal.classList.add('hidden');
        }
        PromptBudget.release('feedback');
        document.removeEventListener('keydown', this._onModalKeydown);
        this._focusBefore?.focus?.();
        this._focusBefore = null;
    };

    _dismissModal = () => {
        this._closeModal();
        AnalyticsService.trackFeedbackPromptDismissed(this._moment);
    };

    _initModalEvents = () => {
        if (this._modalBound) {
            return;
        }
        this._modalBound = true;

        const $modal = document.getElementById(this._reviewModalElement);
        const $form = $modal.querySelector('#review-form');
        const $stars = $modal.querySelectorAll('.star');

        $modal.querySelector('.review-modal-overlay')?.addEventListener('click', this._dismissModal);
        $modal.querySelector('.review-modal-close')?.addEventListener('click', this._dismissModal);
        $modal.querySelector('#review-cancel')?.addEventListener('click', () => {
            this._dismissModal();
            this._dismissPopup();
        });

        $stars.forEach(($star) => {
            $star.addEventListener('click', () => {
                this._selectedRating = parseInt($star.dataset.rating);
                this._updateStars($stars, this._selectedRating);
                // The rating chosen, not the ones lit up to it: a screen reader hears which one is pressed.
                $stars.forEach(($other) => $other.setAttribute('aria-pressed', String($other === $star)));
            });

            $star.addEventListener('mouseenter', () => {
                const rating = parseInt($star.dataset.rating);
                this._updateStars($stars, rating);
            });
        });

        $modal.querySelector('.review-stars')?.addEventListener('mouseleave', () => {
            this._updateStars($stars, this._selectedRating);
        });

        $form?.addEventListener('submit', (event) => {
            event.preventDefault();

            this._submitReview($form);
        });
    };

    _updateStars = ($stars, rating) => {
        $stars.forEach(($star, index) => {
            if (index < rating) {
                $star.classList.add('active');
            } else {
                $star.classList.remove('active');
            }
        });
    };

    // The answer of the visible question, the rating and the comment are each optional, not all three.
    _submitReview = ($form) => {
        const formData = new FormData($form);
        const intent = $form.querySelector('.review-intent:not(.hidden) input:checked')?.value;
        const comment = (formData.get('comment') || '').trim();
        const rating = this._selectedRating || undefined;

        if (!intent && !rating && !comment) {
            Notification.warning(I18n.t('js.review.note'));
            return;
        }

        const $submitBtn = $form.querySelector('#review-submit');
        if ($submitBtn) {
            $submitBtn.disabled = true;
            $submitBtn.textContent = I18n.t('js.review.sending');
        }

        const data = {moment: this._moment, intent, rating, comment, path: window.location.pathname};

        // Add email if user is not connected
        const email = formData.get('email');
        if (email) {
            data.email = email;
        }

        const moment = this._moment;
        sendReview(data)
            .then((response) => {
                // A refusal resolves {errors}: never "thanks", and the visitor is asked again later
                if (isFailure(response)) {
                    if (needsErrorToast(response)) {
                        Notification.error(I18n.t('js.review.error'));
                    }
                    return;
                }

                this._safeSetItem('review_submitted', 'true');
                this._safeSetItem('feedback_answered_at', Date.now().toString());
                AnalyticsService.trackFeedbackSubmitted(moment, intent, rating);
                Notification.success(I18n.t('js.review.thanks'));
                this._closeModal();
                this._hidePopup();
                this._stopScrollListener();
            })
            .catch(() => {
                Notification.error(I18n.t('js.review.error'));
            })
            .finally(() => {
                if ($submitBtn) {
                    $submitBtn.disabled = false;
                    $submitBtn.textContent = I18n.t('js.review.send');
                }
            });
    };

    // The pill stays away for _daysSinceDismissed, on this page too.
    _dismissPopup = () => {
        this._safeSetItem('review_dismissed', 'true');
        this._safeSetItem('review_dismissed_time', Date.now().toString());
        this._hidePopup();
        this._stopScrollListener();
    };

    _hidePopup = () => {
        const $reviewPopup = document.getElementById(this._reviewPopupElement);
        if ($reviewPopup) {
            $reviewPopup.classList.add('hidden');
        }
    };

    // Tests only: a fresh manager on a fresh page.
    _reset = () => {
        document.removeEventListener('keydown', this._onModalKeydown);
        this._momentListeners.forEach(([type, listener]) => window.removeEventListener(type, listener));
        this._timers.forEach(clearTimeout);
        this._stopScrollListener();
        this._momentListeners = [];
        this._timers = [];
        this._pillBound = false;
        this._modalBound = false;
        this._hasScrolledEnough = false;
        this._selectedRating = 0;
        this._moment = 'pill';
        this._pageItems = {};
    };
}

export default new ReviewManager();
